import * as React from 'react';

import { cva, VariantProps } from 'class-variance-authority';
import { Link } from '@remix-run/react';

const paginationLinkVariants = cva(
    'rocco-pagination-link inline-flex items-center justify-center whitespace-nowrap rounded-full text-base font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            variant: {
                outline: 'border border-input bg-gray-900 text-gray-50',
                ghost: 'md:hover:bg-[#d8d8d8]',
                disabled: 'opacity-15 cursor-not-allowed',
            },
            type: {
                default: 'size-11.5',
                previous: 'size-11.5',
                next: 'size-11.5',
            },
        },
        defaultVariants: {
            variant: 'ghost',
            type: 'default',
        },
    },
);

type PaginationLinkProps = React.ComponentProps<typeof Link> &
    VariantProps<typeof paginationLinkVariants> & {
        isActive?: boolean;
        isDisabled?: boolean; // Added support for disabling link
    };

export const PaginationLink = ({
    isActive,
    isDisabled,
    type,
    ...props
}: PaginationLinkProps) => {
    const linkProps = isDisabled
        ? { 'aria-disabled': true, disabled: true, tabIndex: -1 }
        : {};

    if (isDisabled) {
        return (
            <span
                className={paginationLinkVariants({
                    variant: 'disabled',
                    type,
                })}
            >
                {props.children}
            </span>
        );
    }

    return (
        <Link
            aria-current={isActive ? 'page' : undefined}
            className={paginationLinkVariants({
                variant: isActive ? 'outline' : 'ghost',
                type,
            })}
            {...linkProps}
            {...props}
        />
    );
};

PaginationLink.displayName = 'PaginationLink';
